import { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

/**
 * Contatori per i badge di navigazione dell'Area Cliente.
 *
 * Una sola chiamata per tutti i badge del drawer. Prima esisteva solo il
 * contatore dei messaggi non letti, e per ottenerlo `NotificationsContext`
 * scaricava `?limit=100` notifiche complete: qui arriva solo il numero.
 */
export interface DashboardCounters {
  ordiniInLavorazione: number;
  /** Ordini con una novità che l'utente non ha ancora visto: il chip di
   *  "I miei ordini". Si azzera aprendo l'ordine o con "Segna tutti come visti". */
  ordiniAggiornati: number;
  messaggiNonLetti: number;
}

const EMPTY: DashboardCounters = { ordiniInLavorazione: 0, ordiniAggiornati: 0, messaggiNonLetti: 0 };

/** Chi cambia ciò che i contatori contano (un ordine visto, "Segna tutti
 *  come visti") lo annuncia: il layout li ricarica. */
export const EVENTO_CONTATORI = 'dtk:contatori';
export function aggiornaContatori() {
  window.dispatchEvent(new Event(EVENTO_CONTATORI));
}

export function useDashboardCounters(enabled = true) {
  const [data, setData] = useState<DashboardCounters>(EMPTY);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const res = await api.get('/api/clienti/me/dashboard/counters');
      setData({ ...EMPTY, ...(res.data?.counts ?? {}) });
    } catch {
      // I badge sono informativi: se il conteggio non arriva la
      // navigazione deve funzionare comunque, senza badge e senza errore.
      setData(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const ricarica = () => { void load(); };
    window.addEventListener(EVENTO_CONTATORI, ricarica);
    return () => window.removeEventListener(EVENTO_CONTATORI, ricarica);
  }, [load]);

  return { data, loading, reload: load };
}
